import {
  ActionableNotification,
  Button,
  ClickableTile,
  IconButton,
  InlineNotification,
  SkeletonText,
} from '@carbon/react';
import {
  CheckmarkFilled,
  Edit,
  Map as MapIcon,
  WarningFilled,
} from '@carbon/react/icons';
import classNames from 'classnames';
import { useMemo, useRef, useState } from 'react';
import { useStore } from '@tanstack/react-form';
import { format0 } from 'utils/number';
import {
  DomainMissingLine,
  NothingToValueRow,
  PendingCoverageList,
} from './case-coverage-v2';
import { PlainLink } from 'components/link';
import {
  type WorkingConfigForm,
  useCaseCoverageList,
  useDomainScenarios,
  useEvaluationCaseDetail,
  usePackageName,
  usePriceDecks,
  useRunScenarios,
  useTypeCurveParams,
} from './hooks';
import { CaseDomainRows } from './case-domain-rows';
import { ExpenseCoverageNotification } from './expense-coverage/expense-coverage-notification';
import { needsRecheck, runExpenseRow } from './expense-coverage/utils';
import {
  coverageBand,
  formatCoveragePct,
  formDomainOverrides,
  isFullyCovered,
  runSlotOverridesWire,
  runVerdictText,
} from './utils';
import { coverageReadout } from './coverage-utils';
import { useOptionalEval3Page } from './context';
import type { RunOverrides } from 'lib/evaluation-case/package-context';
import { ViewPropertySchedule } from './view-property-schedule';
import type { EvaluationCaseCoverage, ScenarioPickerContext } from './types';
import { TypeCurveScenarioSection } from './type-curve-scenario-section';
import { CaseCoverageTearsheet } from './case-coverage-tearsheet';
import styles from './case-management.module.scss';

export interface CaseManagementProps {
  /** The page's package (pushed down from the section — the coverage reads scope to it). */
  packageId: number;
  /** The lifted working-config form handle (`evaluationCaseId` / overrides / deck pick). */
  workingConfig: WorkingConfigForm;
  /** Open the Case Picker drill-in (the Select CTA and the name's edit pencil). */
  onOpenCasePicker: () => void;
  /** Open the Uncovered drill-in for the ACTIVE case (coverage tile / View Missing). */
  onOpenUncovered: () => void;
  /** Open the Scenario Picker drill-in for one (area × domain). */
  onOpenScenarioPicker: (context: ScenarioPickerContext) => void;
  /** Open the Price Deck picker drill-in (the Price Deck row's Edit). */
  onOpenPriceDeckPicker: () => void;
}

/**
 * The coverage tile's accessible name, carrying what a sighted user reads on it: the sentence for
 * nothing left to value, "All assets covered", or the percent and where to see what is missing.
 *
 * @param {EvaluationCaseCoverage} entry - The committed case's coverage row.
 * @param {boolean} fullyCovered - Nothing in the package is uncovered.
 * @return {string} The label.
 */
function coverageTileLabel(
  entry: EvaluationCaseCoverage,
  fullyCovered: boolean
): string {
  if (entry.nothingToValue) return entry.nothingToValueText ?? '';
  if (fullyCovered) return 'All assets covered';
  return `${formatCoveragePct(entry.coveragePct)}% covers this package. See missing assets`;
}

/**
 * The Case management section content (the main panel view): the active-case
 * selector over the coverage warning + tile, and the scenario assumptions, one
 * row per domain (P4 FR#1). No case selected renders the
 * "Select an Evaluation Case" CTA alone (FR#2); a selected case renders the
 * helper label, the case name from `useEvaluationCaseDetail` (skeleton while it
 * loads — the selector itself renders immediately off the form id), the
 * name's edit pencil, and the case's COVERAGE (FR#5): a
 * partial-coverage warning ("N% of this package won't be valued.") over a
 * clickable tile — health-banded bar, "[X]% covers this package", and the
 * all-covered / "Missing N assets, N units, N slots" row — whose whole surface
 * opens the Uncovered drill-in ("View Missing" is presentational, the T8 a11y
 * pattern). Both render only once the active case's `coverage_list` entry
 * resolves (a cache hit when the pick came through the picker). A detail-fetch
 * failure surfaces as a section-scoped notification + Retry (never a page
 * block). The remaining sections fill in in later slices — see the placeholder
 * markers below.
 *
 * @param {CaseManagementProps} props - The page identity, form handle, and drill-in openers.
 * @return {JSX.Element} The Case management section content.
 */
export function CaseManagement({
  packageId,
  workingConfig,
  onOpenCasePicker,
  onOpenUncovered,
  onOpenScenarioPicker,
  onOpenPriceDeckPicker,
}: CaseManagementProps) {
  const { setTypeCurveScenario, setTypeCurveOverrides } = workingConfig;
  // Reactive read of the selected case — the one field this shell renders from.
  const evaluationCaseId = useStore(
    workingConfig.form.store,
    (state) => state.values.evaluationCaseId
  );
  // INS-10669 — the picked Type Curve Scenario (`null` = the BE's Legacy default) and whether
  // any slot overrides are pending, which gates the wipe confirm on a change.
  // The EFFECTIVE scenario, not the raw pick: with no explicit pick the dropdown must show the
  // case's own scenario, which is what the run will use (INS-10669).
  const typeCurveScenarioId = useTypeCurveParams(workingConfig);
  const hasPendingSlotOverrides = useStore(
    workingConfig.form.store,
    (state) => state.values.slotOverrides.length > 0
  );
  // The reopened run had per-area overrides, which no longer apply.
  const droppedAreaOverrides = useStore(
    workingConfig.form.store,
    (state) => state.values.droppedAreaOverrides
  );
  // The run's "This run only" picks, which the coverage tearsheet previews in place of the case's.
  const caseOverrides = useStore(
    workingConfig.form.store,
    (state) => state.values.evaluationCaseOverrides
  );
  // Its `{ domain: id }` picks, as Calculate sends them (formDomainOverrides), once per change.
  const runPicks = useMemo(
    () => formDomainOverrides(caseOverrides),
    [caseOverrides]
  );
  // The override log a Calculate of this eval would send, and the eval it would re-run: the
  // tearsheet counts the package's slots under them, as the run would value them.
  const calculationId = useOptionalEval3Page()?.calculationId ?? null;
  const committedSlotOverrides = useStore(
    workingConfig.form.store,
    (state) => state.values.committedSlotOverrides
  );
  const pendingSlotOverrides = useStore(
    workingConfig.form.store,
    (state) => state.values.slotOverrides
  );
  const scheduleSlotOverrides = useStore(
    workingConfig.form.store,
    (state) => state.values.scheduleSlotOverrides
  );
  const runOverrides = useMemo<RunOverrides>(
    () => ({
      slotOverrides: runSlotOverridesWire({
        committedSlotOverrides,
        slotOverrides: pendingSlotOverrides,
        scheduleSlotOverrides,
      }),
      calculationId,
    }),
    [
      committedSlotOverrides,
      pendingSlotOverrides,
      scheduleSlotOverrides,
      calculationId,
    ]
  );
  const caseSelected = evaluationCaseId !== null;
  // The case's coverage map for this package, in a tearsheet off the map icon. Mounted from its
  // first open (a closed one still renders its header), then kept, so a close returns focus.
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [coverageMounted, setCoverageMounted] = useState(false);
  const coverageButtonRef = useRef<HTMLButtonElement>(null);
  const packageName = usePackageName(packageId);
  // What this run would use per domain, and whether it can start (EvaluationCase::Readiness, with
  // the run's overrides).
  const runScenarios = useRunScenarios(workingConfig);
  const {
    data: caseDetail,
    isPending: detailLoading,
    isError: detailError,
    refetch: refetchDetail,
  } = useEvaluationCaseDetail(evaluationCaseId);

  // Prefetch the Price Deck picker's list (FR#9), so the row's Edit opens on a cache hit. Keyed
  // on the case's OWN deck, which the list forces in even when archived; the picker asks with the
  // same key. Gated on a case being selected.
  usePriceDecks(caseDetail?.priceDeckId ?? null, { enabled: caseSelected });

  // Prefetch every domain scenario list: gated on a case, cached 30 min + keyed by domain, so the
  // scenario picker (T14) is a cache hit.
  useDomainScenarios('development_pace', { enabled: caseSelected });
  useDomainScenarios('taxes', { enabled: caseSelected });
  useDomainScenarios('differentials', { enabled: caseSelected });
  useDomainScenarios('discount_rates', { enabled: caseSelected });
  // The run's Expenses row, for the expense-coverage callout. Rows kept from earlier overrides
  // only while the expenses pick is unchanged: otherwise they may name the replaced scenario.
  const expenseRow = runExpenseRow(
    runScenarios.data?.domains,
    runScenarios.isPreviousData,
    runPicks
  );
  const expenseScenarioId = expenseRow?.scenarioId ?? null;
  // Expenses ALSO re-read on window focus, so a gap fixed or added in another tab shows here
  // without waiting out the 30-minute cache: at most every ten seconds while gapped, once a
  // minute while covered or missing from the list (needsRecheck).
  const expenses = useDomainScenarios('expenses', {
    enabled: caseSelected,
    refetchOnWindowFocus: (query) =>
      needsRecheck(
        query.state.data,
        expenseScenarioId,
        query.state.dataUpdatedAt
      )
        ? 'always'
        : false,
  });

  // The active case's coverage entry — the SAME cache entry the picker reads
  // (one threshold=0 fetch per package), so arriving from a pick is a cache
  // hit. Absent until the list resolves, which keeps the warning + tile absent
  // (design: no skeleton here). A list fetch error is deliberately quiet in
  // this section — the picker owns the coverage error + Retry surface.
  //
  // NOTE (INS-12104): the BE used to omit zero-overlap cases, so a 0% case
  // could never be the active one and this stayed undefined. It now returns a
  // row for every case, because omitting one made "does not apply to this
  // package" indistinguishable from "does not exist". A committed 0% case
  // therefore renders the warning at its maximum — "100% of this package won't
  // be valued." — over a 0% tile, where previously there was nothing. That is
  // intentional and accurate, but it is a UI state this section was originally
  // designed never to reach; revisit the copy if it reads badly in practice.
  const { data: coverageData, error: coverageError } =
    useCaseCoverageList(packageId);
  const coverageEntry: EvaluationCaseCoverage | undefined = [
    ...(coverageData?.aboveThreshold ?? []),
    ...(coverageData?.belowThreshold ?? []),
  ].find((row) => row.id === evaluationCaseId);
  // One named fact for the warning gate, the tile's accessible name, and the
  // all-covered vs breakdown branch (counts-authoritative — see isFullyCovered).
  const fullyCovered =
    coverageEntry !== undefined && isFullyCovered(coverageEntry);
  const readout = coverageEntry && coverageReadout(coverageEntry);
  const nothingToValue = coverageEntry?.nothingToValue === true;
  // The warning's shortfall, pre-formatted: `coveragePct` is one-decimal
  // rounded, so a barely-uncovered case (99.96 → 100) yields "0" here — the
  // title falls back to "Less than 0.1%…" so it never contradicts the
  // missing-counts breakdown beside it.
  const shortfallPct = coverageEntry
    ? formatCoveragePct(100 - coverageEntry.coveragePct)
    : '';
  // The active case may not be counted yet (case-level coverage comes from a cache the BE warms),
  // and a counted one may be incomplete. A failed list (a 503 coverage_unavailable once the warm
  // gave up) says so here, instead of leaving the last poll's "calculating" up, since polling
  // stops on failure.
  const coverageFailed = coverageError !== null;
  const pendingEntries = {
    calculating: !coverageFailed
      ? (coverageData?.calculating ?? []).filter(
          (row) => row.id === evaluationCaseId
        )
      : [],
    unavailable: (coverageData?.unavailable ?? []).filter(
      (row) => row.id === evaluationCaseId
    ),
  };

  // No case → the Select CTA is the section's ONLY content (FR#2); everything
  // else (warning / tile / deck / scenario rows) appears once a case is selected.
  if (evaluationCaseId === null) {
    return (
      <div className={styles.section}>
        <Button
          className={styles.selectButton}
          kind="primary"
          size="md"
          onClick={onOpenCasePicker}
        >
          Select an Evaluation Case
        </Button>
        {/* The schedule affordance shows here too, disabled with the schedule-first hint
            until a case is picked (04-GNT §5.4). */}
        <ViewPropertySchedule
          evaluationCaseId={null}
          workingConfig={workingConfig}
        />
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.selector}>
        <span className={styles.helperLabel}>Active evaluation case</span>
        <div className={styles.caseRow}>
          {detailLoading ? (
            <SkeletonText className={styles.caseNameSkeleton} width="12rem" />
          ) : caseDetail ? (
            // The case's own management page, in a new tab so the user does not
            // lose the workspace's in-progress configuration. Uses the BE's own
            // `href` rather than rebuilding the route here, like the domain rows
            // below do with `scenarioHref`. PlainLink bundles the target/rel
            // security pair.
            // The case page as Case Management opens it, with no package: its map shows the
            // case's coverage alone. This package's slots are in the coverage tearsheet.
            <PlainLink
              className={styles.caseNameLink}
              href={caseDetail.href}
              openInNewTab
            >
              {caseDetail.name}
            </PlainLink>
          ) : // Detail failed to load — the section-scoped error below says why,
          // and there is no href to link to.
          null}
          {/* Same edit affordance as the per-area domain rows below, so every
              changeable assumption in this panel is reached the same way. */}
          <IconButton
            className={styles.caseEdit}
            label="Change evaluation case"
            kind="ghost"
            size="sm"
            align="left"
            onClick={onOpenCasePicker}
          >
            <Edit />
          </IconButton>
          {caseDetail && (
            <IconButton
              ref={coverageButtonRef}
              className={styles.caseEdit}
              label="View coverage for this package"
              kind="ghost"
              size="sm"
              align="left"
              onClick={() => {
                setCoverageMounted(true);
                setCoverageOpen(true);
              }}
            >
              <MapIcon />
            </IconButton>
          )}
        </div>
        {caseDetail && coverageMounted && (
          <CaseCoverageTearsheet
            open={coverageOpen}
            onClose={() => setCoverageOpen(false)}
            launcherButtonRef={coverageButtonRef}
            caseId={caseDetail.id}
            caseName={caseDetail.name}
            caseHref={caseDetail.href}
            packageName={packageName}
            packageContext={{ packageId, typeCurveScenarioId }}
            overrides={runPicks}
            runOverrides={runOverrides}
          />
        )}
        {/* (13.2) Why a run of this case can't start, the same text as the Calculate gate's. */}
        {runScenarios.data &&
          !runScenarios.isPreviousData &&
          !runScenarios.data.complete && (
            <p
              className={styles.caseIncomplete}
              data-testid="active-case-incomplete"
            >
              {runVerdictText(runScenarios.data)}
            </p>
          )}
        {/* Opens the "Property development schedule" dialog for the active case; enabled
            as soon as a case is selected (pre-run reads on demand, 04-GNT §5.4). */}
        <ViewPropertySchedule
          evaluationCaseId={evaluationCaseId}
          workingConfig={workingConfig}
        />
      </div>

      {/* Section-scoped detail error — the selector stays usable (the user can
          still change case); Retry re-runs the detail read. */}
      {detailError && (
        <ActionableNotification
          className={styles.notification}
          inline
          kind="error"
          // hasFocus off: its auto-focus scrolls the errored section into view (page jump)
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load the evaluation case"
          subtitle="The case details are unavailable right now."
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetchDetail()}
        />
      )}

      {droppedAreaOverrides && (
        <InlineNotification
          className={styles.notification}
          kind="info"
          lowContrast
          hideCloseButton
          title="This run's per-area overrides no longer apply; it now runs on the case's scenarios."
        />
      )}

      {/* Partial-coverage warning (FR#5) — the picker's banner was the general
          heads-up; this makes the COMMITTED case's shortfall concrete. */}
      {coverageEntry && nothingToValue && (
        <InlineNotification
          className={styles.notification}
          kind="info"
          lowContrast
          hideCloseButton
          title={coverageEntry.nothingToValueText}
        />
      )}
      {coverageEntry && !fullyCovered && !nothingToValue && (
        <InlineNotification
          className={styles.notification}
          kind="warning"
          lowContrast
          hideCloseButton
          title={
            shortfallPct === '0'
              ? "Less than 0.1% of this package won't be valued."
              : `${shortfallPct}% of this package won't be valued.`
          }
        />
      )}

      {coverageFailed && (
        <InlineNotification
          className={styles.notification}
          kind="error"
          lowContrast
          hideCloseButton
          title="Coverage unavailable"
          subtitle={
            coverageError instanceof Error
              ? coverageError.message
              : 'Coverage could not be computed for this package.'
          }
        />
      )}

      <PendingCoverageList
        calculating={pendingEntries.calculating}
        unavailable={pendingEntries.unavailable}
      />

      {/* Coverage tile (FR#5) — the WHOLE tile is the single activator opening
          the Uncovered drill-in; "View Missing" is presentational (aria-hidden),
          per the T8 pattern (no nested interactive inside an interactive). */}
      {coverageEntry && (
        <ClickableTile
          className={styles.coverageTile}
          // Carries the percent a sighted user sees on the same surface, so
          // the accessible name isn't less informative than the visible tile.
          aria-label={coverageTileLabel(coverageEntry, fullyCovered)}
          onClick={onOpenUncovered}
        >
          <div className={styles.coverageRow}>
            <div className={styles.coverageTrack}>
              {typeof readout?.fillPct === 'number' && (
                <div
                  data-testid="coverage-fill"
                  className={classNames(
                    styles.coverageFill,
                    styles[coverageBand(readout.fillPct)]
                  )}
                  // Dynamic width — the one value CSS can't predetermine.
                  style={{ width: `${readout.fillPct}%` }}
                />
              )}
            </div>
            <span className={styles.coveragePct}>{readout?.pctText}</span>
            <span className={styles.coverageLabel}>{readout?.label}</span>
          </div>
          {nothingToValue ? (
            <NothingToValueRow text={coverageEntry.nothingToValueText} />
          ) : fullyCovered ? (
            <div className={styles.allCoveredRow}>
              <CheckmarkFilled
                aria-hidden="true"
                className={styles.allCoveredIcon}
              />
              <span>All assets covered</span>
            </div>
          ) : (
            <div className={styles.breakdownRow}>
              <span className={styles.breakdownText}>
                <WarningFilled
                  aria-hidden="true"
                  className={styles.breakdownIcon}
                />
                Missing <strong>{format0(coverageEntry.missing.assets)}</strong>{' '}
                assets, <strong>{format0(coverageEntry.missing.units)}</strong>{' '}
                units, <strong>{format0(coverageEntry.missing.slots)}</strong>{' '}
                slots
              </span>
              {/* Visual affordance only — the TILE is the activator (its
                  aria-label names the action), so this carries no handler and
                  stays out of the a11y tree. */}
              <span className={styles.breakdownLink} aria-hidden="true">
                View Missing
              </span>
            </div>
          )}
          {!fullyCovered && !nothingToValue && (
            <DomainMissingLine domainMissing={coverageEntry.domainMissing} />
          )}
        </ClickableTile>
      )}

      {/* (13.3) Scenario assumptions: one row per domain, case-wide. An override, including a
          Price Deck pick (the run's overrides + top-level `priceDeckId`, buildCalculateRequest),
          is this run's only; the case keeps its own. */}
      <div className={styles.scenariosSection}>
        <span className={styles.helperLabel}>Scenario assumptions</span>
        <CaseDomainRows
          rows={runScenarios.data?.domains}
          loading={runScenarios.isLoading}
          // A failed refetch for these same overrides keeps the rows it had. A failure with none,
          // or with only the previous overrides' rows, shows the error: those rows aren't this run's.
          error={
            runScenarios.isError &&
            (!runScenarios.data || runScenarios.isPreviousData)
              ? runScenarios.error
              : null
          }
          onRetry={runScenarios.refetch}
          onEdit={(domain, currentScenarioId) =>
            onOpenScenarioPicker({
              areaId: null,
              areaName: 'This run',
              domain,
              currentScenarioId,
            })
          }
          onEditPriceDeck={onOpenPriceDeckPicker}
          // Type Curve (INS-10669) is the case's: the row names it, and what it brings into the
          // package and Manage Type Curves sit under it. The section keeps its scenario-change
          // confirm, which clears the pending slot overrides (they key on the replaced
          // scenario's place ids).
          typeCurveDetails={
            <TypeCurveScenarioSection
              packageId={packageId}
              typeCurveScenarioId={typeCurveScenarioId}
              hasPendingOverrides={hasPendingSlotOverrides}
              onScenarioChange={setTypeCurveScenario}
              onTypeCurveOverridesChange={setTypeCurveOverrides}
              calculationId={calculationId}
              readOnly
            />
          }
        />
        {/* Expense coverage (INS-12686): the run's expense scenario's areas the calculator
            charges $0 for at least one of LOE / oil / gas / water opex, rather than falling back
            to the legacy Scenario's cost. Scenario-intrinsic, so it can warn before the run. */}
        <ExpenseCoverageNotification
          expenseRow={expenseRow}
          expenseScenarios={expenses.data}
          expenseScenariosError={expenses.isError}
          onRetry={() => expenses.refetch()}
        />
      </div>
    </div>
  );
}
